import { Query } from '@apollo/client/react/components';
import { DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { LensFieldDetailsQuery, LensFieldDetailsQueryVariables, FieldDetailsFragment, LensFieldDetailsDocument } from '../../../../../gen-src/queries';
import { FieldDetailsFieldName, FieldDetailsPane, FieldDetailsProperties, FieldDetailsTypeRow, RenderTypeSpecificNonDatasourceFieldDetails } from '../FieldDetails/FieldDetailsDialog';
import { ConnectedDatasourceField, FieldPropertyDescription } from '../FieldDetails/FieldPropertyComponents';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { RenderQueryResult } from '../renderQueryResult';

const dialogWidth = 500;

export interface LensFieldDetailsDialogProps {
  readonly fieldId: string;
  readonly makeFieldUrl: (fieldId: string) => string;
  readonly onClose: () => void;
  readonly lensLuid: string;
}

export class LensFieldDetailsDialog extends React.Component<LensFieldDetailsDialogProps> {

  public override render(): JSX.Element {
    return (
      <Query<LensFieldDetailsQuery, LensFieldDetailsQueryVariables>
        query={LensFieldDetailsDocument}
        variables={{ lensFieldId: this.props.fieldId }}
      >
        {result => <RenderQueryResult result={result} renderers={{ renderData: this.renderLensFieldDetails }} />}
      </Query>
    );
  }

  private readonly renderLensFieldDetails = (data: LensFieldDetailsQuery): React.ReactNode => {
    const field = data.lensFields && data.lensFields[0];
    if (!field) {
      return null;
    }
    const fieldIcon = iconForField(field.datasourceField);

    const datasource = field?.datasourceField?.datasource;
    const datasourceVizPortalUrlId = datasource?.__typename === 'PublishedDatasource' ? datasource?.vizportalUrlId : null;

    return (
      <DialogWidget
        testId='field-details'
        title={Messages.FieldDetails()}
        type={DialogType.NormalWithTitleBar}
        positioningBehavior={DialogPositioningBehavior.Centered}
        dialogWidth={dialogWidth}
        showCloseButton={true}
        showFooter={false}
        isModal={true}
        onCloseRequest={this.props.onClose}
        handleGlassClick={this.props.onClose}
      >
        <FieldDetailsPane>
          <FieldDetailsFieldName>{field.name || field.datasourceField.name}</FieldDetailsFieldName>
          <FieldDetailsTypeRow>
            <SvgFieldIcon fieldIcon={fieldIcon} />
            {fieldIcon ? FieldNameMappings[fieldIcon]() : null}
          </FieldDetailsTypeRow>
          <FieldDetailsProperties>
            <FieldPropertyDescription labelFunc={Messages.Description} description={field.description || field.datasourceField.description} />
            {this.renderTypeSpecificFieldDetails(field.datasourceField)}
            <ConnectedDatasourceField
              datasourceVizPortalUrlId={datasourceVizPortalUrlId}
              fieldId={field?.datasourceField.id}
              fieldName={field?.datasourceField?.name} />
          </FieldDetailsProperties>
        </FieldDetailsPane>
      </DialogWidget>
    );
  }

  private renderTypeSpecificFieldDetails(field: FieldDetailsFragment): React.ReactNode {
    if (field.__typename !== 'DatasourceField') {
      return RenderTypeSpecificNonDatasourceFieldDetails(field, this.props.makeFieldUrl);
    } else if (field.remoteField) {
      return RenderTypeSpecificNonDatasourceFieldDetails(field.remoteField, this.props.makeFieldUrl);
    } else {
      return null;
    }
  }
}
